/*
 * Focus is "lost" when the element that held it left the DOM (a closed dialog's close button, a toggle
 * that lived in the layout that was just swapped out) and the document fell back to <body>. Returning
 * focus only in that case leaves alone a user who has already moved it somewhere else.
 */
export const isFocusLost = () => !document.activeElement || document.activeElement === document.body;

const tryFocus = (target: HTMLElement | null | undefined, options?: FocusOptions) => {
  if (!target) return false;
  target.focus(options);
  return document.activeElement === target;
};

const USER_ACTIONS = ['pointerdown', 'keydown'] as const;

/*
 * Hands lost focus to the target, and keeps handing it back while the page settles: the target may
 * render a commit or a portal later, sit disabled until a search settles, or be replaced when new
 * results render, which drops focus to <body> again. Does nothing if focus was not lost to begin with,
 * and stops at the user's first pointer or key press (from then on, where focus goes is theirs) or
 * after the timeout. Returns a cancel function, so it can be an effect's cleanup.
 */
export const returnFocusWhenAvailable = (
  getTarget: () => HTMLElement | null | undefined,
  { timeout = 10000, ...options }: FocusOptions & { timeout?: number } = {}
): (() => void) => {
  if (!isFocusLost()) return () => {};

  const attempt = () => {
    if (isFocusLost()) tryFocus(getTarget(), options);
  };
  const observer = new MutationObserver(attempt);
  const cancel = () => {
    observer.disconnect();
    clearTimeout(timer);
    USER_ACTIONS.forEach((type) => document.removeEventListener(type, cancel, true));
  };
  const timer = setTimeout(cancel, timeout);
  USER_ACTIONS.forEach((type) => document.addEventListener(type, cancel, true));

  attempt();
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
  return cancel;
};
